/** @format */

import {
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Avatar,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import BlockIcon from "@mui/icons-material/Block";
import InfoIcon from "@mui/icons-material/Info";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store/store";
import {
	getAutoDraftUsersAsAdmin,
	getAutoDraftUserByIdAsAdmin,
	getAutoDraftSummaryAsAdmin,
	cancelAutoDraftAsAdmin,
	AutoDraftUserInfo,
	CircleAutoDraftInfo,
} from "../../../redux/actions/autoDraftAction";
import {
	LoadingSpinner,
	SuccessNotification,
} from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { selectAllUsersAsAdmin } from "../../../redux/selectors/userSelector";

const AllUsersAutoDraft = () => {
	const dispatch = useDispatch<AppDispatch>();

	// Selectors for auto-draft state
	const autoDraftUsers = useSelector(
		(state: RootState) => state.user.autoDraftUsers,
	);
	const autoDraftSummary = useSelector(
		(state: RootState) => state.user.autoDraftSummary,
	);
	const autoDraftLoading = useSelector(
		(state: RootState) => state.user.autoDraftLoading,
	);
	const allUsersData = useSelector(selectAllUsersAsAdmin);

	// Helper function to get user profile image from allUsers store
	const getUserProfileImage = (userId: string): string | undefined => {
		const user = allUsersData?.users?.find((u) => u.userId === userId);
		return user?.profileImage;
	};

	const [openNav, setOpenNav] = useState(false);
	const [processingUserId, setProcessingUserId] = useState<string | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchQuery, setSearchQuery] = useState("");
	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		type: "cancel" | null;
		userId: string | null;
		userName: string;
	}>({
		open: false,
		type: null,
		userId: null,
		userName: "",
	});
	const [detailsModal, setDetailsModal] = useState<{
		open: boolean;
		user: AutoDraftUserInfo | null;
	}>({
		open: false,
		user: null,
	});
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});
	const [circlesPage, setCirclesPage] = useState(0);
	const [circlesRowsPerPage, setCirclesRowsPerPage] = useState(4);

	useEffect(() => {
		dispatch(getAutoDraftUsersAsAdmin());
		dispatch(getAutoDraftSummaryAsAdmin());
	}, [dispatch]);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
		setPage(0);
	};

	const handleRefresh = async () => {
		try {
			await Promise.all([
				dispatch(getAutoDraftUsersAsAdmin()).unwrap(),
				dispatch(getAutoDraftSummaryAsAdmin()).unwrap(),
			]);
			setNotification({
				open: true,
				message: "Auto-draft data refreshed successfully",
				severity: "success",
			});
		} catch (err: any) {
			setNotification({
				open: true,
				message:
					err?.message ||
					err?.toString() ||
					"Failed to refresh auto-draft data",
				severity: "error",
			});
		}
	};

	const handleOpenConfirmDialog = (
		type: "cancel",
		userId: string,
		userName: string,
	) => {
		setConfirmDialog({
			open: true,
			type,
			userId,
			userName,
		});
	};

	const handleCloseConfirmDialog = () => {
		if (!isProcessing) {
			setConfirmDialog({
				open: false,
				type: null,
				userId: null,
				userName: "",
			});
		}
	};

	const handleConfirmAction = async () => {
		if (!confirmDialog.userId || !confirmDialog.type) return;

		setIsProcessing(true);
		setProcessingUserId(confirmDialog.userId);

		try {
			if (confirmDialog.type === "cancel") {
				await dispatch(cancelAutoDraftAsAdmin(confirmDialog.userId)).unwrap();
			}

			setNotification({
				open: true,
				message: "Auto-draft cancelled successfully for user",
				severity: "success",
			});

			handleCloseConfirmDialog();
			await Promise.all([
				dispatch(getAutoDraftUsersAsAdmin()).unwrap(),
				dispatch(getAutoDraftSummaryAsAdmin()).unwrap(),
			]);
		} catch (err: any) {
			setNotification({
				open: true,
				message: err?.message || "Failed to cancel auto-draft",
				severity: "error",
			});
		} finally {
			setIsProcessing(false);
			setProcessingUserId(null);
		}
	};

	const handleOpenDetailsModal = async (user: AutoDraftUserInfo) => {
		setDetailsModal({
			open: true,
			user,
		});
		setCirclesPage(0);

		// Fetch detailed user info
		try {
			const detailedUser = await dispatch(
				getAutoDraftUserByIdAsAdmin(user.userId),
			).unwrap();
			setDetailsModal({
				open: true,
				user: detailedUser,
			});
		} catch (err) {
			// Keep using the basic user info if detailed fetch fails
			console.error("Failed to fetch detailed user info:", err);
		}
	};

	const handleCloseDetailsModal = () => {
		setDetailsModal({
			open: false,
			user: null,
		});
		setCirclesPage(0);
	};

	const handleCirclesPageChange = (
		event: React.MouseEvent<HTMLButtonElement> | null,
		newPage: number,
	) => {
		setCirclesPage(newPage);
	};

	const handleCirclesRowsPerPageChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setCirclesRowsPerPage(parseInt(event.target.value, 10));
		setCirclesPage(0);
	};

	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const users = autoDraftUsers || [];
	const summary = autoDraftSummary?.statistics;

	const filteredUsers = users.filter((user) => {
		const searchLower = searchQuery.toLowerCase();
		const firstNameMatch = user.firstName?.toLowerCase().includes(searchLower);
		const lastNameMatch = user.lastName?.toLowerCase().includes(searchLower);
		const emailMatch = user.email?.toLowerCase().includes(searchLower);
		return firstNameMatch || lastNameMatch || emailMatch;
	});

	const paginatedUsers = filteredUsers.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const getPaymentStatusColor = (
		hasPaymentMethod: boolean,
	): "success" | "error" => {
		return hasPaymentMethod ? "success" : "error";
	};

	const getPaymentStatusLabel = (hasPaymentMethod: boolean): string => {
		return hasPaymentMethod ? "Valid" : "Missing";
	};

	const getActiveCirclesCount = (circles: CircleAutoDraftInfo[]): number => {
		return circles.filter((c) => c.hasActiveCycle).length;
	};

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{autoDraftLoading && !users.length ?
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={autoDraftLoading} size={45} />
						</Box>
					:	<>
							<Stack spacing={3}>
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">Auto-Draft Users</Typography>
										<Typography color="text.secondary" variant="body2">
											Manage users with auto-draft enabled
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Tooltip title="Refresh">
											<IconButton
												onClick={handleRefresh}
												disabled={autoDraftLoading}>
												<ArrowsClockwise />
											</IconButton>
										</Tooltip>
									</Box>
								</Stack>

								{/* Stats Cards */}
								<Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
									<Card sx={{ flex: 1, p: 2, minWidth: 150 }}>
										<Stack direction="row" spacing={1} alignItems="center">
											<AutorenewIcon color="primary" />
											<Box>
												<Typography variant="h6" color="primary">
													{summary?.totalUsersWithAutoDraft || 0}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Total Auto-Draft Users
												</Typography>
											</Box>
										</Stack>
									</Card>
									<Card sx={{ flex: 1, p: 2, minWidth: 150 }}>
										<Stack direction="row" spacing={1} alignItems="center">
											<CreditCardIcon color="success" />
											<Box>
												<Typography variant="h6" color="success.main">
													{summary?.usersWithValidPaymentMethod || 0}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Valid Payment Method
												</Typography>
											</Box>
										</Stack>
									</Card>
									<Card sx={{ flex: 1, p: 2, minWidth: 150 }}>
										<Stack direction="row" spacing={1} alignItems="center">
											<CreditCardOffIcon color="error" />
											<Box>
												<Typography variant="h6" color="error.main">
													{summary?.usersWithoutPaymentMethod || 0}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Missing Payment Method
												</Typography>
											</Box>
										</Stack>
									</Card>
									<Card sx={{ flex: 1, p: 2, minWidth: 150 }}>
										<Stack direction="row" spacing={1} alignItems="center">
											<CheckCircleIcon color="info" />
											<Box>
												<Typography variant="h6" color="info.main">
													{summary?.activeCycleMemberships || 0}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Active Cycle Memberships
												</Typography>
											</Box>
										</Stack>
									</Card>
								</Stack>

								{/* Search */}
								<Card>
									<Stack
										direction="row"
										spacing={2}
										sx={{ p: 2 }}
										alignItems="center">
										<OutlinedInput
											value={searchQuery}
											onChange={handleSearchChange}
											fullWidth
											placeholder="Search by name or email..."
											startAdornment={
												<InputAdornment position="start">
													<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
												</InputAdornment>
											}
										/>
									</Stack>
								</Card>

								{/* Users Table */}
								<Card>
									{autoDraftLoading ?
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												minHeight: 400,
											}}>
											<CircularProgress />
										</Box>
									:	<>
											<Box sx={{ overflowX: "auto" }}>
												<Table sx={{ minWidth: "800px" }}>
													<TableHead>
														<TableRow>
															<TableCell>User</TableCell>
															<TableCell>Email</TableCell>
															<TableCell>Payment Status</TableCell>
															<TableCell>Payment Type</TableCell>
															<TableCell>Circles</TableCell>
															<TableCell>Active Cycles</TableCell>
															<TableCell align="center">More Info</TableCell>
															<TableCell align="center">Actions</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{paginatedUsers.length === 0 ?
															<TableRow>
																<TableCell colSpan={8} align="center">
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		No auto-draft users found
																	</Typography>
																</TableCell>
															</TableRow>
														:	paginatedUsers.map((user: AutoDraftUserInfo) => (
																<TableRow hover key={user.userId}>
																	<TableCell>
																		<Stack
																			direction="row"
																			spacing={2}
																			alignItems="center">
																			<Avatar
																				src={getUserProfileImage(user.userId)}
																				sx={{ width: 40, height: 40 }}>
																				{`${user.firstName?.[0]}${user.lastName?.[0]}`}
																			</Avatar>
																			<Typography variant="subtitle2">
																				{`${user.firstName} ${user.lastName}`}
																			</Typography>
																		</Stack>
																	</TableCell>
																	<TableCell>{user.email}</TableCell>
																	<TableCell>
																		<Chip
																			label={getPaymentStatusLabel(
																				user.hasDefaultPaymentMethod,
																			)}
																			color={getPaymentStatusColor(
																				user.hasDefaultPaymentMethod,
																			)}
																			size="small"
																			icon={
																				user.hasDefaultPaymentMethod ?
																					<CreditCardIcon />
																				:	<CreditCardOffIcon />
																			}
																		/>
																	</TableCell>
																	<TableCell>
																		{user.defaultPaymentMethodType || "N/A"}
																	</TableCell>
																	<TableCell>
																		{user.circles?.length || 0}
																	</TableCell>
																	<TableCell>
																		<Chip
																			label={getActiveCirclesCount(
																				user.circles || [],
																			)}
																			color={
																				(
																					getActiveCirclesCount(
																						user.circles || [],
																					) > 0
																				) ?
																					"success"
																				:	"default"
																			}
																			size="small"
																		/>
																	</TableCell>
																	<TableCell align="center">
																		<Tooltip title="More Info">
																			<IconButton
																				color="primary"
																				size="small"
																				onClick={() =>
																					handleOpenDetailsModal(user)
																				}>
																				<InfoIcon />
																			</IconButton>
																		</Tooltip>
																	</TableCell>
																	<TableCell>
																		<Stack
																			direction="row"
																			spacing={1}
																			justifyContent="center">
																			<Tooltip title="Cancel Auto-Draft">
																				<span>
																					<IconButton
																						color="warning"
																						size="small"
																						disabled={
																							isProcessing &&
																							processingUserId === user.userId
																						}
																						onClick={() =>
																							handleOpenConfirmDialog(
																								"cancel",
																								user.userId,
																								`${user.firstName} ${user.lastName}`,
																							)
																						}>
																						{(
																							isProcessing &&
																							processingUserId === user.userId
																						) ?
																							<CircularProgress size={20} />
																						:	<BlockIcon />}
																					</IconButton>
																				</span>
																			</Tooltip>
																		</Stack>
																	</TableCell>
																</TableRow>
															))
														}
													</TableBody>
												</Table>
											</Box>
											<Divider />
											<TablePagination
												component="div"
												count={filteredUsers.length}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
												page={page}
												rowsPerPage={rowsPerPage}
												rowsPerPageOptions={[5, 10, 25, 50]}
											/>
										</>
									}
								</Card>

								{/* Confirmation Dialog */}
								<Dialog
									open={confirmDialog.open}
									onClose={!isProcessing ? handleCloseConfirmDialog : undefined}
									aria-labelledby="confirm-dialog-title">
									<DialogTitle id="confirm-dialog-title">
										Cancel Auto-Draft
									</DialogTitle>
									<DialogContent>
										{isProcessing ?
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													gap: 2,
													py: 3,
												}}>
												<CircularProgress size={40} />
												<Typography variant="body2" color="text.secondary">
													Processing...
												</Typography>
											</Box>
										:	<DialogContentText>
												Are you sure you want to cancel auto-draft for{" "}
												<strong>{confirmDialog.userName}</strong>? This will
												disable automatic payments for all their circles.
											</DialogContentText>
										}
									</DialogContent>
									<DialogActions>
										<Button
											onClick={handleCloseConfirmDialog}
											disabled={isProcessing}>
											No, Keep It
										</Button>
										<Button
											onClick={handleConfirmAction}
											color="warning"
											variant="contained"
											disabled={isProcessing}>
											Yes, Cancel Auto-Draft
										</Button>
									</DialogActions>
								</Dialog>

								{/* User Details Modal */}
								<Dialog
									open={detailsModal.open}
									onClose={handleCloseDetailsModal}
									maxWidth="md"
									fullWidth
									aria-labelledby="details-dialog-title"
									PaperProps={{
										sx: {
											borderRadius: 3,
											overflow: "hidden",
										},
									}}>
									<DialogTitle
										id="details-dialog-title"
										sx={{
											background:
												"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
											color: "white",
											py: 2.5,
										}}>
										<Typography variant="h5" fontWeight={600}>
											Auto-Draft User Details
										</Typography>
									</DialogTitle>
									<DialogContent sx={{ p: 0 }}>
										{detailsModal.user && (
											<Box>
												{/* User Profile Header */}
												<Box
													sx={{
														background:
															"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
														pt: 0,
														pb: 8,
														px: 3,
													}}>
													<Stack
														direction="row"
														spacing={3}
														alignItems="center">
														<Avatar
															src={getUserProfileImage(
																detailsModal.user.userId,
															)}
															sx={{
																width: 80,
																height: 80,
																border: "4px solid white",
																boxShadow: 3,
															}}>
															{`${detailsModal.user.firstName?.[0]}${detailsModal.user.lastName?.[0]}`}
														</Avatar>
														<Box>
															<Typography
																variant="h5"
																fontWeight={600}
																color="white">
																{detailsModal.user.firstName}{" "}
																{detailsModal.user.lastName}
															</Typography>
															<Typography
																variant="body2"
																sx={{ color: "rgba(255,255,255,0.85)" }}>
																{detailsModal.user.email}
															</Typography>
															{detailsModal.user.phone && (
																<Typography
																	variant="body2"
																	sx={{ color: "rgba(255,255,255,0.75)" }}>
																	{detailsModal.user.phone}
																</Typography>
															)}
														</Box>
													</Stack>
												</Box>

												{/* Status Cards - Overlapping Design */}
												<Box sx={{ px: 3, mt: -5 }}>
													<Grid container spacing={2}>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		detailsModal.user.autoPayEnabled ?
																			"linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
																		:	"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
																	color: "white",
																}}>
																<AutorenewIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Auto-Draft Status
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.user.autoPayEnabled ?
																		"Enabled"
																	:	"Disabled"}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		detailsModal.user.hasDefaultPaymentMethod ?
																			"linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
																		:	"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
																	color: "white",
																}}>
																{detailsModal.user.hasDefaultPaymentMethod ?
																	<CreditCardIcon
																		sx={{ fontSize: 32, mb: 1 }}
																	/>
																:	<CreditCardOffIcon
																		sx={{ fontSize: 32, mb: 1 }}
																	/>
																}
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Payment Method
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.user.hasDefaultPaymentMethod ?
																		(
																			detailsModal.user
																				.defaultPaymentMethodType || "Card"
																		).toUpperCase()
																	:	"Not Set"}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
																	color: "white",
																}}>
																<CheckCircleIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Total Circles
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.user.circles?.length || 0}
																</Typography>
															</Card>
														</Grid>
													</Grid>
												</Box>

												{/* User Info Section */}
												<Box sx={{ px: 3, py: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Account Information
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Stack divider={<Divider />}>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Card Last 4 Digits
																</Typography>
																<Typography variant="body1" fontWeight={500}>
																	{detailsModal.user.cardLast4Digits || "N/A"}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Timezone
																</Typography>
																<Typography variant="body1" fontWeight={500}>
																	{detailsModal.user.timezone || "Not Set"}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Active Cycles
																</Typography>
																<Chip
																	label={getActiveCirclesCount(
																		detailsModal.user.circles || [],
																	)}
																	color="success"
																	size="small"
																/>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Inactive Cycles
																</Typography>
																<Chip
																	label={
																		(detailsModal.user.circles?.length || 0) -
																		getActiveCirclesCount(
																			detailsModal.user.circles || [],
																		)
																	}
																	color="default"
																	size="small"
																/>
															</Stack>
														</Stack>
													</Card>
												</Box>

												{/* Circles Section */}
												{detailsModal.user.circles &&
													detailsModal.user.circles.length > 0 && (
														<Box sx={{ px: 3, pb: 3 }}>
															<Typography
																variant="subtitle1"
																fontWeight={600}
																sx={{ mb: 2, color: "text.secondary" }}>
																Circle Memberships (
																{detailsModal.user.circles.length})
															</Typography>
															<Stack spacing={2}>
																{detailsModal.user.circles
																	.slice(
																		circlesPage * circlesRowsPerPage,
																		circlesPage * circlesRowsPerPage +
																			circlesRowsPerPage,
																	)
																	.map((circle) => (
																		<Card
																			key={circle.circleId}
																			variant="outlined"
																			sx={{
																				borderRadius: 2,
																				transition: "all 0.2s ease-in-out",
																				"&:hover": {
																					boxShadow: 2,
																					borderColor: "primary.main",
																				},
																			}}>
																			<Stack
																				direction="row"
																				spacing={2}
																				alignItems="center"
																				sx={{ p: 2 }}>
																				<Avatar
																					alt={circle.circleName}
																					src={circle.circleImage}
																					sx={{
																						width: 56,
																						height: 56,
																						borderRadius: 2,
																					}}
																					variant="rounded"
																				/>
																				<Box sx={{ flex: 1, minWidth: 0 }}>
																					<Stack
																						direction="row"
																						alignItems="center"
																						spacing={1}>
																						<Typography
																							variant="subtitle1"
																							fontWeight={600}
																							noWrap>
																							{circle.circleName}
																						</Typography>
																						{circle.isLeader && (
																							<Tooltip title="Circle Leader">
																								<Chip
																									icon={
																										<StarIcon
																											sx={{
																												color:
																													"gold !important",
																											}}
																										/>
																									}
																									label="Leader"
																									size="small"
																									sx={{
																										bgcolor: "warning.lighter",
																										color: "warning.dark",
																									}}
																								/>
																							</Tooltip>
																						)}
																					</Stack>
																					<Stack
																						direction="row"
																						spacing={2}
																						sx={{ mt: 0.5 }}>
																						<Typography
																							variant="body2"
																							color="text.secondary">
																							Contribution:{" "}
																							<strong>
																								$
																								{circle.contributionAmount?.toFixed(
																									2,
																								) || "0.00"}
																							</strong>
																						</Typography>
																						<Typography
																							variant="body2"
																							color="text.secondary">
																							Days:{" "}
																							<strong>
																								{circle.contributionDays
																									?.map((d) =>
																										d.substring(0, 3),
																									)
																									.join(", ") || "N/A"}
																							</strong>
																						</Typography>
																					</Stack>
																				</Box>
																				<Stack
																					alignItems="flex-end"
																					spacing={1}>
																					<Chip
																						label={
																							circle.hasActiveCycle ?
																								"Active Cycle"
																							:	"No Active Cycle"
																						}
																						color={
																							circle.hasActiveCycle ? "success"
																							:	"default"
																						}
																						size="small"
																						variant={
																							circle.hasActiveCycle ? "filled"
																							:	"outlined"
																						}
																					/>
																					{circle.activeCycleEndDate && (
																						<Typography
																							variant="caption"
																							color="text.secondary">
																							Ends:{" "}
																							{dayjs(
																								circle.activeCycleEndDate,
																							).format("MMM D, YYYY")}
																						</Typography>
																					)}
																				</Stack>
																			</Stack>
																		</Card>
																	))}
															</Stack>
															{detailsModal.user.circles.length >
																circlesRowsPerPage && (
																<TablePagination
																	page={circlesPage}
																	component="div"
																	count={detailsModal.user.circles.length}
																	rowsPerPage={circlesRowsPerPage}
																	onPageChange={handleCirclesPageChange}
																	rowsPerPageOptions={[4, 8, 12]}
																	onRowsPerPageChange={
																		handleCirclesRowsPerPageChange
																	}
																	sx={{ mt: 2 }}
																/>
															)}
														</Box>
													)}
											</Box>
										)}
									</DialogContent>
									<DialogActions sx={{ px: 3, py: 2 }}>
										<Button
											onClick={handleCloseDetailsModal}
											variant="outlined"
											sx={{ borderRadius: 2, px: 3 }}>
											Close
										</Button>
									</DialogActions>
								</Dialog>

								{/* Success/Error Notification */}
								<SuccessNotification
									open={notification.open}
									onClose={() =>
										setNotification((prev) => ({ ...prev, open: false }))
									}
									message={notification.message}
									severity={notification.severity}
								/>
							</Stack>
						</>
					}
				</Main>
			</Box>
		</React.Fragment>
	);
};

export default AllUsersAutoDraft;
